import React, { useState, useRef, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { IconButton, Slider, Tooltip } from '@material-ui/core';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import PauseIcon from '@material-ui/icons/Pause';
import LoopIcon from '@material-ui/icons/Loop';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import Rotate90DegreesCcwIcon from '@material-ui/icons/Rotate90DegreesCcw';

const MAX_WIDTH = window.innerWidth > 600 ? 600 : window.innerWidth;

const useStyles = makeStyles((theme) => ({
  controlBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    padding: theme.spacing(1),
  },
  iconButton: {
    background: 'transparent !important',
    minWidth: '0  !important',
    border: 'none  !important',
    margin: '0  !important',
    padding: '10px  !important',
    color: 'white  !important',
    '& svg': {
      color: 'white  !important',
    }
  },
  sliderRoot: {
    marginLeft: 5,
    marginRight: 5,
  },
  sliderRail: {
    background: 'white  !important',
  },
  sliderTrack: {
    background: 'white  !important',
  },
  sliderThumb: {
    background: 'white  !important',
  },
  loopIconWithStrike: {
    position: 'relative',
    lineHeight: '1 !important',
    marginTop: 1,
  },
  strikeThrough: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    transformOrigin: 'center',
    transform: 'rotate(150deg)',
    marginLeft: '-10px',
    marginTop: '-2px',
    background: 'white',
    width: '82%',
    height: '2px'
  },
}));

const LoopIconWithStrike = (props) => {
  const classes = useStyles();
  return (
    <div className={classes.loopIconWithStrike}>
      <LoopIcon {...props} />
      <div className={classes.strikeThrough} />
    </div>
  );
};

const DraggableCanvas = React.forwardRef((props, ref) => {
  const { isFullscreen, style, handlePlayPause, orbitOnDrag, setOrbitOffsetX } = props;

  const [canvasPosition, setCanvasPosition] = useState({ x: 0, y: 0 });
  const [isDraggingCanvas, setIsDraggingCanvas] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [initialMousePosition, setInitialMousePosition] = useState({ x: 0, y: 0 });

  React.useEffect(() => {
    if (!isFullscreen) {
      setCanvasPosition({ x: 0, y: 0 })
    }
  }, [isFullscreen])

  const handleSliderMouseMove = (event) => {
    if (isDraggingCanvas) {
      const offsetX = event.clientX - initialMousePosition.x;
      const offsetY = event.clientY - initialMousePosition.y;

      setOrbitOffsetX(offsetX);
      // if(orbitOnDrag){
      //   setOrbitOffsetX(offsetX);
      // }else{
      //   setCanvasPosition({
      //     x: canvasPosition.x + offsetX,
      //     y: canvasPosition.y + offsetY,
      //   });

      //   setInitialMousePosition({ x: event.clientX, y: event.clientY });
      // }
    } else if (isMouseDown) {
      setIsDraggingCanvas(true);
    }
  };


  return <canvas
    ref={ref}
    {...props}
    style={{
      ...style,
      transform: `translate(${canvasPosition.x}px, ${canvasPosition.y}px)`,
      cursor: isDraggingCanvas ? 'grabbing' : 'grab'
    }}
    onMouseDown={(event) => {
      setIsMouseDown(true)
      setInitialMousePosition({ x: event.clientX, y: event.clientY });
      // if(orbitOnDrag){
      handlePlayPause('pause');
      //   setOrbitOffsetX(0)
      // }
    }}
    onMouseUp={(event) => {
      setIsMouseDown(false)
      if (isDraggingCanvas) {
        setIsDraggingCanvas(false);
      } else {
        // if(!orbitOnDrag){
        // handlePlayPause();
        // }
      }
      handlePlayPause('play');
    }}
    onMouseMove={handleSliderMouseMove}
    onMouseEnter={() => {
      handlePlayPause('play'); // play animation on mouse hover
    }}
  />
})

const VideoPlayerControls = ({
  onMouseOver, onMouseLeave, style, currentPercentage, setCurrentPercentage,
  isPlaying, handlePlayPause, isLooping, setIsLooping,
  playerContainerRef, isFullscreen, setIsFullscreen,
  handleZoom, handleZoomRelease, zoomLevel, setOrbitOnDrag, orbitOnDrag,
}) => {
  const classes = useStyles();
  const [isDragging, setIsDragging] = useState(false); // Track dragging state
  const sliderRef = useRef(null); // Reference to the Slider component

  useEffect(() => {

    // Add event listener for fullscreen change
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    // Clean up the event listener when the component unmounts
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const handleFullscreenChange = () => {
    // Handle the transition when exiting fullscreen
    setIsFullscreen(document.fullscreenElement)
  };

  const handleFullScreen = () => {

    // Request full screen mode for the container element
    if (!isFullscreen) {
      if (playerContainerRef.current.requestFullscreen) {
        playerContainerRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  const handleSliderMouseDown = () => {
    setIsDragging(true);
  };

  const handleSliderMouseUp = (event, keepDragging) => {
    if (isDragging) {
      const sliderRect = sliderRef.current.getBoundingClientRect();
      const offsetX = event.clientX - sliderRect.left;
      const percentage = (offsetX / sliderRect.width) * 100;

      // Update the current percentage based on the mouse position
      // You may want to clamp the percentage within the valid range (0-100)
      setCurrentPercentage(percentage);

      if (!keepDragging) {
        setIsDragging(false);
      }
    }
  };

  const handleSliderMouseMove = (event) => {
    if (isDragging) {
      handleSliderMouseDown()
      handleSliderMouseUp(event, true)
    }
  }

  return (
    <div className={classes.controlBar} style={style} onMouseOver={onMouseOver} onMouseLeave={onMouseLeave}>
      <IconButton className={classes.iconButton} onClick={() => handlePlayPause(!isPlaying)}>
        {isPlaying ? <Tooltip title="Pause"><PauseIcon /></Tooltip> : <Tooltip title="Play"><PlayArrowIcon /></Tooltip>}
      </IconButton>
      <Slider
        ref={sliderRef}
        value={currentPercentage}
        min={0}
        max={100}
        classes={{
          root: classes.sliderRoot,
          rail: classes.sliderRail,
          track: classes.sliderTrack,
          thumb: classes.sliderThumb,
        }}
        onMouseDown={handleSliderMouseDown}
        onMouseMove={handleSliderMouseMove}
        onMouseUp={handleSliderMouseUp}
      />
      <IconButton className={classes.iconButton} onClick={() => setOrbitOnDrag(!orbitOnDrag)}>
        {!orbitOnDrag ? <Tooltip title="Enable: Hold and drag to orbit"><Rotate90DegreesCcwIcon style={{ opacity: 0.6 }} /></Tooltip> : <Tooltip title="Disable: Hold and drag to orbit"><Rotate90DegreesCcwIcon /></Tooltip>}
      </IconButton>
      <IconButton className={classes.iconButton} onClick={() => setIsLooping(!isLooping)}>
        {!isLooping ? <Tooltip title="Enable: Looping"><LoopIconWithStrike /></Tooltip> : <Tooltip title="Disable: Looping"><LoopIcon color={'primary'} /></Tooltip>}
      </IconButton>
      <Tooltip title="Zoom out"><IconButton className={classes.iconButton} onMouseDown={() => handleZoom(-0.1)} onMouseUp={handleZoomRelease}>
        <ZoomOutIcon style={{ opacity: zoomLevel === 1 ? 0.6 : 1 }} />
      </IconButton></Tooltip>
      <Tooltip title="Zoom in"><IconButton className={classes.iconButton} onMouseDown={() => handleZoom(0.1)} onMouseUp={handleZoomRelease}>
        <ZoomInIcon />
      </IconButton></Tooltip>
      <IconButton className={classes.iconButton} onClick={handleFullScreen}>
        {isFullscreen ? <Tooltip title="Exit full screen" placement="top"><FullscreenExitIcon /></Tooltip> : <Tooltip title="Enter full screen"><FullscreenIcon /></Tooltip>}
      </IconButton>
    </div>
  );
};

class ImagePlayer extends React.Component {
  constructor(props) {
    super(props);
    this.playerContainerRef = React.createRef();
    this.canvasRef = React.createRef();
    this.currentIndex = 0;
    this.currentAnimationIndex = 0;
    this.isPlaying = false;
    this.imageCache = []; // Cache for storing loaded images
    this.prevMouseX = null; // for the orbit rotation by mouse drag and move
    this.state = {
      currentPercentage: 0,
      isPlaying: false,
      isLooping: true,
      showControls: true,
      zoomLevel: 1,
      orbitOnDrag: true,
      aspectRatio: 758 / 597, // defaults to rectangle which is the very first 360 we tested with
    };
  }

  componentDidMount() {
    this.getFirstImageAspectRatio(aspectRatio => {
      this.setState({aspectRatio}, () => {
        this.loadImage();
      })
    });
  }

  getFirstImageAspectRatio(onComplete=(aspectRatio)=>{}) {
    const { images = [] } = this.props;
    const img = new Image();
    img.src = images[0];

    img.onload = () => {
      const ratio = img.width / img.height;
      onComplete(ratio.toFixed(2))
    };

    img.onerror = () => {
      onComplete(this.state.aspectRatio)
    };
  }

  componentWillUnmount() {
    cancelAnimationFrame(this.animationFrameId);
  }

  loadImage() {
    const { images = [] } = this.props;

    if (this.currentIndex >= images.length) return; // Reached the end of the image sequence

    const image = new Image();
    image.onload = () => {
      // Add the loaded image to the cache
      this.imageCache.push(image);

      // Increment the current index
      this.currentIndex++;

      // if (this.currentIndex >= images.length - 1) { // show animation once all images are loaded
      if (this.currentIndex == 1) { // show first image right away, before all images are loaded
        if (!this.animationStarted) {
          this.startAnimation();
        }
      }

      this.loadImage();
    };
    image.src = images[this.currentIndex];
  }

  hideControls() {
    setTimeout(() => {
      this.setState({ showControls: false })
    }, 2000)
  }

  showControls() {
    this.setState({ showControls: true })
  }

  startAnimation() {
    this.animationStarted = true;

    this.hideControls();

    let frameCount = 0;
    let startTime = performance.now();

    const canvas = this.canvasRef.current;
    const context = canvas.getContext('2d');

    const update = () => {

      // Reached the end of the image sequence
      const currentAnimationPercentage = (this.currentAnimationIndex / (this.imageCache.length - 1)) * 100;
      this.setState({ currentPercentage: currentAnimationPercentage })
      // console.log('animation index', this.currentAnimationIndex, 'of', this.imageCache.length -1, currentAnimationPercentage)
      if (this.currentAnimationIndex === this.imageCache.length - 1) {
        this.currentAnimationIndex = 0;
        if (!this.state.isLooping) {
          // use this to stop animation at the end without replay
          this.setState({ isPlaying: false })
        }
      }

      // Clear the canvas
      context.clearRect(0, 0, canvas.width, canvas.height);

      // Redraw the canvas with the cached images sequentially
      this.drawImageFromCache(context, this.currentAnimationIndex);

      // Increase the frame count
      frameCount++;

      // Calculate the elapsed time
      const currentTime = performance.now();
      const elapsedTime = currentTime - startTime;

      if (elapsedTime >= 50) {
        // Reset the frame count and time
        frameCount = 0;
        startTime = currentTime;

        // Call your function or perform specific actions here at the desired frame rate
        if (this.state.isPlaying) {
          this.currentAnimationIndex++;
        }

        // For example, log the frame rate
        // console.log('Frame Rate:', 1000 / elapsedTime);

      }
      // Call the update function again on the next frame
      this.animationFrameId = requestAnimationFrame(update);
    }

    // Start the update loop
    this.animationFrameId = requestAnimationFrame(update);
  }

  drawImageFromCache(context, index) {
    const { canvasWidth, canvasHeight } = this.getContainerDimensions();

    // Clear the canvas
    const canvas = this.canvasRef.current;
    context.clearRect(0, 0, canvas.width, canvas.height);

    const image = this.imageCache[index];
    const destWidth = canvasWidth * this.state.zoomLevel;
    const destHeight = canvasHeight * this.state.zoomLevel;

    // Draw the image from the cache
    context.drawImage(image, 0, 0, destWidth, destHeight);
    this.setState({ destWidth, destHeight })
  }

  goToPercentage(newPercentage) {
    this.currentAnimationIndex = Math.ceil((newPercentage / 100) * (this.imageCache.length - 1));
    if (this.currentAnimationIndex < 0) {
      this.currentAnimationIndex = 0;
    }
    if (this.currentAnimationIndex > this.imageCache.length - 1) {
      this.currentAnimationIndex = this.imageCache.length - 2;
    }
  }

  setOrbitOffsetX(offsetX) {

    if (this.prevMouseX !== null) {
      if (offsetX > this.prevMouseX) {
        this.currentAnimationIndex--
      } else if (offsetX < this.prevMouseX) {
        this.currentAnimationIndex++
      }
    }

    this.prevMouseX = offsetX;

    if (this.currentAnimationIndex < 0) {
      this.currentAnimationIndex = this.imageCache.length - 2;
    }
    if (this.currentAnimationIndex > this.imageCache.length - 1) {
      this.currentAnimationIndex = this.imageCache.length - 2;
    }

  }

  handleZoom = (delta) => {
    const increment = delta * 0.3; // Adjust the increment as needed
    this.adjustZoom(increment);
  };

  adjustZoom = (increment) => {
    let newZoomLevel = this.state.zoomLevel + increment;
    if (newZoomLevel < 1) newZoomLevel = 1;
    const clampedZoomLevel = Math.max(0.1, Math.min(newZoomLevel, 3)); // Limit zoom between 0.1 and 3

    this.setState({ zoomLevel: clampedZoomLevel });

    if (this.zoomTimeout) {
      clearTimeout(this.zoomTimeout);
    }

    this.zoomTimeout = setTimeout(() => {
      this.adjustZoom(increment);
    }, 50); // Adjust the timeout duration as needed
  };
  handleZoomRelease = () => {
    clearTimeout(this.zoomTimeout);
  };

  getContainerDimensions() {
    let { height } = this.props;
    const { aspectRatio } = this.state;

    let width = aspectRatio * height;

    if(width > MAX_WIDTH){
      width = MAX_WIDTH;
    } 

    if (this.state.isFullscreen) {
      width = window.innerWidth;
    }

    let canvasWidth = width;
    let canvasHeight = width;

    // Calculate new dimensions based on the aspect ratio
    if (canvasWidth / canvasHeight > aspectRatio) {
      canvasWidth = canvasHeight * aspectRatio;
    } else {
      canvasHeight = canvasWidth / aspectRatio;
    }
    return {
      canvasWidth,
      canvasHeight,
    }
  }

  render() {
    const { noControlsAndLogo } = this.props;
    const { currentPercentage, isLooping, isPlaying, showControls, isFullscreen, destWidth, destHeight, zoomLevel, orbitOnDrag } = this.state;

    const { canvasWidth, canvasHeight } = this.getContainerDimensions();

    return (
      <div
        ref={this.playerContainerRef}
        style={{
          position: 'relative',
          width: canvasWidth,
          height: canvasHeight,
          overflow: 'hidden', // Prevents content from overflowing when zoomed in
          background: 'white',
        }}
        onMouseLeave={() => this.hideControls()}
      >
        <DraggableCanvas
          ref={this.canvasRef}
          style={{
            display: 'block',
            margin: '0 auto',
          }}
          width={destWidth}
          height={destHeight}
          handlePlayPause={(action) => this.setState({ isPlaying: action ? (action === 'pause' ? false : true) : !isPlaying })}
          isFullscreen={isFullscreen}
          orbitOnDrag={orbitOnDrag}
          setOrbitOffsetX={offsetX => this.setOrbitOffsetX(offsetX)}
        />

        {/* {!noControlsAndLogo && <VideoPlayerControls
          currentPercentage={currentPercentage}
          setCurrentPercentage={newPercentage => this.goToPercentage(newPercentage)}
          isPlaying={isPlaying}
          handlePlayPause={isPlaying => this.setState({ isPlaying })}
          isLooping={isLooping}
          setIsLooping={isLooping => this.setState({ isLooping })}
          playerContainerRef={this.playerContainerRef}
          style={{ transition: 'opacity 1s', opacity: showControls ? 1 : 0 }}
          isFullscreen={isFullscreen}
          setIsFullscreen={isFullscreen => this.setState({isFullscreen})}
          orbitOnDrag={orbitOnDrag}
          setOrbitOnDrag={orbitOnDrag => this.setState({orbitOnDrag})}
          handleZoom={zoom => this.handleZoom(zoom)}
          handleZoomRelease={this.handleZoomRelease}
          zoomLevel={zoomLevel}
          onMouseOver={() => this.showControls()}
          onMouseLeave={() => this.hideControls()}
        />} */}

        {!noControlsAndLogo && <img src="/images/hd-logo-tiny.png" style={{
          position: 'absolute',
          left: '16px',
          top: '18px',
          width: '40px',
          height: '50px',
        }} />}
      </div>
    );
  }
}

export default ImagePlayer;
